import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  FlexProps,
  Image,
  ImageProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { SelectField, ShowCaseTrailerModal } from "@picovico/ui";
import { getLocalStorageData, storeLocalStorageData } from "@picovico/utils";
import { format, isSameDay, isToday } from "date-fns";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { BsPlayCircleFill } from "react-icons/bs";

import { getPageRoutes } from "@/routes/pageRoutes";
import { useGetCinemaHallListQuery } from "@/store/services/cinemahall";
import { useGetNowShowingQuery } from "@/store/services/movie";
import { SHOW_BOOKING_CLOSED } from "@picovico/ui/constant/alertMessage";
import {
  getShowStatusColorScheme,
  isShowViewable,
  sortAdvanceBookingFirst,
} from "@picovico/ui/constant/enums";

const DAY_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;

export interface MovieScheduleGridProps {
  // Filter bar
  filterBarProps?: FlexProps;
  filterGroupProps?: FlexProps;
  filterLabelProps?: TextProps;
  selectFieldWrapperProps?: BoxProps;
  selectChakraStyles?: Record<string, Record<string, unknown>>;

  // Movie list
  movieListProps?: FlexProps;

  // Movie card
  movieCardProps?: FlexProps;

  // Poster
  posterBoxProps?: BoxProps;
  movieImageProps?: ImageProps;

  // Movie info panel
  movieInfoBoxProps?: FlexProps;
  movieTitleProps?: TextProps;
  movieTagChipProps?: BoxProps;
  movieDurationChipProps?: BoxProps;
  movieGenreProps?: TextProps;
  movieGradeProps?: BoxProps;
  movieDescriptionProps?: TextProps;
  movieActionRowProps?: FlexProps;
  trailerButtonProps?: BoxProps;
  detailButtonProps?: BoxProps;

  // Date grid
  dateGridOuterProps?: BoxProps;
  dateGridInnerProps?: FlexProps;
  dateColumnActiveProps?: FlexProps;
  dateColumnInactiveProps?: FlexProps;
  dateDayLabelProps?: TextProps;
  dateDateLabelProps?: TextProps;
  dateColumnDividerProps?: BoxProps;

  // No-shows states
  showEmptyProps?: BoxProps;

  // Showtime slots
  showSlotProps?: BoxProps;
  showTimeItemProps?: ButtonProps;
  showTimeActiveColor?: string;
  showTimeExpiredColor?: string;
  showTimeTextProps?: TextProps;
  showAudiNameProps?: TextProps;
  showQualityBadgeProps?: BoxProps;
  showQualityTextProps?: TextProps;

  noShowsMessageProps?: TextProps;

  /** List advance-booking movies before the rest. */
  prioritizeAdvanceBooking?: boolean;
}

const buildSelectStyles = (
  styles?: Record<string, Record<string, unknown>>,
) => ({
  control: (base: object) => ({
    ...base,
    border: "none",
    _focus: { border: "none", boxShadow: "none" },
    ...styles?.control,
  }),
  singleValue: (base: object) => ({ ...base, ...styles?.singleValue }),
  placeholder: (base: object) => ({ ...base, ...styles?.placeholder }),
  input: (base: object) => ({ ...base, ...styles?.input }),
  menu: (base: object) => ({ ...base, ...styles?.menu }),
  menuList: (base: object) => ({ ...base, ...styles?.menuList }),
  option: (base: object, state: any) => {
    const opts = (styles?.option ?? {}) as Record<string, unknown>;
    const hoverCfg = (opts._hover ?? {}) as Record<string, unknown>;
    const selectedCfg = (opts._selected ?? {}) as Record<string, unknown>;
    const defaultBg = (opts.background ?? opts.bg) as string | undefined;
    const hoverBg = (hoverCfg.background ?? hoverCfg.bg ?? defaultBg) as
      | string
      | undefined;
    const selectedBg = (selectedCfg.background ??
      selectedCfg.bg ??
      defaultBg) as string | undefined;
    const activeBg = state.isSelected
      ? selectedBg
      : state.isFocused
        ? hoverBg
        : defaultBg;
    const excluded = new Set(["_hover", "_selected", "background", "bg"]);
    const restOpts = Object.fromEntries(
      Object.entries(opts).filter(([k]) => !excluded.has(k)),
    );
    return {
      ...base,
      ...restOpts,
      ...(activeBg ? { background: activeBg, bg: activeBg } : {}),
    };
  },
});

const MovieScheduleGrid = ({
  filterBarProps,
  filterGroupProps,
  filterLabelProps,
  selectFieldWrapperProps,
  selectChakraStyles,
  movieListProps,
  movieCardProps,
  posterBoxProps,
  movieImageProps,
  movieInfoBoxProps,
  movieTitleProps,
  movieTagChipProps,
  movieDurationChipProps,
  movieGenreProps,
  movieGradeProps,
  movieDescriptionProps,
  movieActionRowProps,
  trailerButtonProps,
  detailButtonProps,
  dateGridOuterProps,
  dateGridInnerProps,
  dateColumnActiveProps,
  dateColumnInactiveProps,
  dateDayLabelProps,
  dateDateLabelProps,
  dateColumnDividerProps,
  showEmptyProps,
  showSlotProps,
  showTimeItemProps,
  showTimeTextProps,
  showAudiNameProps,
  showQualityBadgeProps,
  showQualityTextProps,
  noShowsMessageProps,
  prioritizeAdvanceBooking = false,
}: MovieScheduleGridProps) => {
  const [audiFilter, setAudiFilter] = useState("all");
  const [selectedCinemaHall, setSelectedCinemaHall] = useState<string>(
    getLocalStorageData("cinema_hall") ?? "",
  );
  const router = useRouter();

  // Cinema hall list — used only for defaulting to first hall; no selector UI
  const { data: hallsData } = useGetCinemaHallListQuery({});
  const cinemaHalls = useMemo(() => hallsData?.data || [], [hallsData]);

  // Default to first hall if nothing stored
  useEffect(() => {
    if (!selectedCinemaHall && cinemaHalls.length > 0) {
      const first = cinemaHalls[0].idx;
      setSelectedCinemaHall(first);
      storeLocalStorageData("cinema_hall", first);
    }
  }, [cinemaHalls, selectedCinemaHall]);

  // Movies for selected hall
  const { data: moviesData, isLoading } = useGetNowShowingQuery(
    {
      show_date: format(new Date(), "yyyy-MM-dd"),
      cinema_hall: selectedCinemaHall,
      advance_days: true,
    },
    { skip: !selectedCinemaHall },
  );

  const movies = useMemo(() => {
    const list = moviesData?.data || [];
    return prioritizeAdvanceBooking
      ? sortAdvanceBookingFirst(list, (m) => new Date(m.release_date))
      : list;
  }, [moviesData, prioritizeAdvanceBooking]);

  // Unique auditoriums across all shows
  const availableAuditoriums = useMemo(() => {
    const map = new Map<string, string>();
    movies.forEach((m) =>
      m.showinfo.forEach((s) => map.set(s.auditorium.idx, s.auditorium.name)),
    );
    return Array.from(map.entries()).map(([idx, name]) => ({ idx, name }));
  }, [movies]);

  const showAudiSelect = availableAuditoriums.length > 1;

  const filteredMovies = useMemo(() => {
    return movies
      .map((m) => ({
        ...m,
        showinfo:
          audiFilter === "all"
            ? m.showinfo
            : m.showinfo.filter((s) => s.auditorium.idx === audiFilter),
      }))
      .filter((m) => m.showinfo.length > 0);
  }, [movies, audiFilter]);

  // Continuous day range from today to the last show date across filtered movies
  const dates = useMemo(() => {
    let maxDate: Date | null = null;
    filteredMovies.forEach((m) =>
      m.showinfo.forEach((s) => {
        const d = new Date(s.show_start_date_time);
        if (!maxDate || d > maxDate) maxDate = d;
      }),
    );
    if (!maxDate) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(maxDate);
    end.setHours(0, 0, 0, 0);
    const days: Date[] = [];
    const cur = new Date(today);
    while (cur <= end) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, [filteredMovies]);

  const handleBooking = (movieIdx: string, showIdx: string) => {
    router.push(getPageRoutes("audi_detail", { movieIdx, showIdx }));
  };

  const handleDetail = (
    movieIdx: string,
    movieinfo: { show_start_date: string; show_end_date: string },
  ) => {
    router.push(
      getPageRoutes(
        "movie_detail",
        { movieIdx, cinemaHallIdx: selectedCinemaHall },
        {
          date: format(new Date(), "yyyy-MM-dd"),
          startDate: movieinfo?.show_start_date,
          endDate: movieinfo?.show_end_date,
        },
      ),
    );
  };

  const audiOptions = [
    { label: "All Auditoriums", value: "all" },
    ...availableAuditoriums.map((a) => ({ label: a.name, value: a.idx })),
  ];

  const chakraStyles = buildSelectStyles(selectChakraStyles);

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" py="3rem">
        <Text>Loading...</Text>
      </Flex>
    );
  }

  return (
    <Box>
      {/* ── Filter Bar ── */}
      <Flex
        gap="1.5rem"
        align="flex-start"
        flexWrap="wrap"
        mb="1.25rem"
        {...filterBarProps}
      >
        {/* Auditorium select — only when > 1 audi */}
        {showAudiSelect && (
          <Flex direction="column" gap="0.3rem" {...filterGroupProps}>
            <Text
              fontSize="11px"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.5px"
              {...filterLabelProps}
            >
              Auditorium
            </Text>
            <Box
              minW="180px"
              borderRadius="6px"
              overflow="hidden"
              {...selectFieldWrapperProps}
            >
              <SelectField
                options={audiOptions}
                value={
                  audiOptions.find((o) => o.value === audiFilter) ||
                  audiOptions[0]
                }
                onChange={(option: any) =>
                  option && setAudiFilter(option.value)
                }
                isSearchable={false}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (provided: any) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
                chakraStyles={chakraStyles}
              />
            </Box>
          </Flex>
        )}
      </Flex>

      {/* ── No shows message ── */}
      {!isLoading && !filteredMovies.length && selectedCinemaHall && (
        <Flex justifyContent="center" alignItems="center" py="3rem">
          <Text fontSize="20px" {...noShowsMessageProps}>
            No Show Available
          </Text>
        </Flex>
      )}

      {/* ── Movie List ── */}
      <Flex direction="column" gap="0.75rem" {...movieListProps}>
        {filteredMovies.map((movie) => {
          return (
            <Flex
              key={movie.idx}
              overflow="hidden"
              direction={{ base: "column", md: "row" }}
              {...movieCardProps}
            >
              {/* Poster */}
              <Box
                flexShrink={0}
                w={{ base: "100%", md: "150px" }}
                h={{ base: "200px", md: "auto" }}
                minH={{ md: "180px" }}
                {...posterBoxProps}
              >
                <Image
                  src={movie.poster}
                  alt={movie.name}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  {...movieImageProps}
                />
              </Box>

              {/* Movie Info */}
              <Flex
                direction="column"
                flexShrink={0}
                w={{ base: "100%", md: "240px" }}
                p="0.85rem"
                justify="space-between"
                {...movieInfoBoxProps}
              >
                <Box>
                  <Text
                    fontSize="16px"
                    fontWeight="700"
                    lineHeight="1.25"
                    mb="0.45rem"
                    {...movieTitleProps}
                  >
                    {movie.name}
                  </Text>

                  <Flex gap="0.35rem" flexWrap="wrap" mb="0.5rem">
                    {movie.tag && (
                      <Box
                        px="0.5rem"
                        py="0.1rem"
                        fontSize="10px"
                        fontWeight="700"
                        borderRadius="3px"
                        {...movieTagChipProps}
                      >
                        {movie.tag}
                      </Box>
                    )}
                    <Box
                      px="0.5rem"
                      py="0.1rem"
                      fontSize="10px"
                      fontWeight="600"
                      borderRadius="3px"
                      {...movieDurationChipProps}
                    >
                      {Math.floor(movie.duration / 60)}h {movie.duration % 60}
                      min
                    </Box>
                    {movie.grade && (
                      <Box
                        px="0.5rem"
                        py="0.1rem"
                        fontSize="10px"
                        fontWeight="700"
                        borderRadius="3px"
                        {...movieGradeProps}
                      >
                        {movie.grade}
                      </Box>
                    )}
                  </Flex>

                  {movie.genre && (
                    <Text fontSize="11px" mb="0.5rem" {...movieGenreProps}>
                      {movie.genre}
                    </Text>
                  )}

                  {/* Description — always rendered; empty string when not provided */}
                  <Text
                    fontSize="12px"
                    lineHeight="1.65"
                    noOfLines={5}
                    {...movieDescriptionProps}
                  >
                    {movie.description}
                  </Text>
                </Box>

                <Flex
                  gap="0.5rem"
                  mt="0.75rem"
                  flexWrap="wrap"
                  {...movieActionRowProps}
                >
                  {movie.trailer && (
                    <ShowCaseTrailerModal
                      url={movie.trailer}
                      actionEl={
                        <Box
                          as="span"
                          cursor="pointer"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="26px"
                          lineHeight="1"
                          color="surface.300"
                          {...trailerButtonProps}
                        >
                          <BsPlayCircleFill />
                        </Box>
                      }
                    />
                  )}
                  <Box
                    cursor="pointer"
                    px="0.75rem"
                    py="0.3rem"
                    fontSize="11px"
                    fontWeight="700"
                    borderRadius="4px"
                    onClick={() => handleDetail(movie.idx, movie.movieinfo)}
                    {...detailButtonProps}
                  >
                    Details
                  </Box>
                </Flex>
              </Flex>

              {/* Date Grid */}
              <Box flex={1} h="100%" {...dateGridOuterProps}>
                <Flex
                  flexWrap="wrap"
                  alignItems="stretch"
                  h="100%"
                  {...dateGridInnerProps}
                >
                  {dates.map((date) => {
                    const todayFlag = isToday(date);
                    const dayShows = movie.showinfo
                      .filter((s) => isSameDay(s.show_start_date_time, date))
                      .sort((a, b) =>
                        a.show_start_date_time.localeCompare(
                          b.show_start_date_time,
                        ),
                      );

                    return (
                      <Flex
                        key={date.toISOString()}
                        direction="column"
                        minW="90px"
                        p="0.65rem"
                        {...(todayFlag
                          ? dateColumnActiveProps
                          : dateColumnInactiveProps)}
                      >
                        {/* Date header */}
                        <Box
                          pb="0.4rem"
                          mb="0.5rem"
                          borderBottom="1px solid"
                          borderColor="whiteAlpha.100"
                          {...dateColumnDividerProps}
                        >
                          <Text
                            fontSize="10px"
                            fontWeight="800"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            {...dateDayLabelProps}
                          >
                            {todayFlag ? "TODAY" : DAY_SHORT[date.getDay()]}
                          </Text>
                          <Text
                            fontSize="10px"
                            mt="1px"
                            {...dateDateLabelProps}
                          >
                            {format(date, "MMM d")}
                          </Text>
                        </Box>

                        {/* Showtime slots */}
                        <Flex direction="column" gap="1.25rem" alignItems={'center'}>
                          {dayShows.length === 0 ? (
                            <Box minH="8px" {...showEmptyProps} />
                          ) : (
                            dayShows.map((show) => {
                              // A locked or already-started show keeps its
                              // status colour and stays clickable — the seat
                              // layout opens read-only.
                              const isViewable = isShowViewable(
                                show.show_status,
                                show.show_ended,
                                show.seat_view_allowed,
                              );
                              const colorScheme =
                                getShowStatusColorScheme(show.show_status);

                              return (
                                <Box
                                  key={show.show_idx}
                                  position="relative"
                                  {...showSlotProps}
                                >
                                  {/* Showtime button */}
                                  <Button
                                    borderRadius="5px"
                                    isDisabled={
                                      !isViewable &&
                                      show.show_status !== "sold"
                                    }
                                    colorScheme={colorScheme}
                                    opacity={
                                      show.lock_web && isViewable
                                        ? 0.75
                                        : undefined
                                    }
                                    title={
                                      show.lock_web && isViewable
                                        ? SHOW_BOOKING_CLOSED
                                        : undefined
                                    }
                                    cursor={
                                      !isViewable ? "not-allowed" : "pointer"
                                    }
                                    pointerEvents={
                                      !isViewable ? "none" : "auto"
                                    }
                                    onClick={() =>
                                      isViewable &&
                                      handleBooking(movie.idx, show.show_idx)
                                    }
                                    {...showTimeItemProps}
                                  >
                                    <Flex
                                      direction={"column"}
                                      alignItems={"center"}
                                    >
                                      <Text {...showTimeTextProps}>
                                        {format(
                                          show.show_start_date_time,
                                          "hh:mm a",
                                        )}
                                      </Text>
                                      <Text
                                        fontSize="9px"
                                        opacity={0.8}
                                        lineHeight="1.3"
                                        {...showAudiNameProps}
                                      >
                                        {show.auditorium.name}
                                      </Text>
                                    </Flex>
                                  </Button>

                                  {/* Quality badge — straddles bottom edge of button */}
                                  {show.video_quality && (
                                    <Flex
                                      position="absolute"
                                      bottom="-10px"
                                      left="50%"
                                      transform="translateX(-50%)"
                                      zIndex={1}
                                      h="20px"
                                      align="center"
                                      justify="center"
                                      px="0.5rem"
                                      borderRadius="full"
                                      bg="rgba(0,0,0,0.55)"
                                      whiteSpace="nowrap"
                                      pointerEvents="none"
                                      {...showQualityBadgeProps}
                                    >
                                      <Text
                                        fontSize="9px"
                                        fontWeight="700"
                                        textTransform="uppercase"
                                        letterSpacing="0.4px"
                                        lineHeight="1"
                                        {...showQualityTextProps}
                                      >
                                        {show.video_quality}
                                      </Text>
                                    </Flex>
                                  )}
                                </Box>
                              );
                            })
                          )}
                        </Flex>
                      </Flex>
                    );
                  })}
                </Flex>
              </Box>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};

export default MovieScheduleGrid;
